import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import type { PublicProduct } from '@mopani/contracts';
import { fetchProducts } from '../../lib/api';
import { EmptyState, ErrorState, Skeleton } from './primitives';

/**
 * Product grid — database-backed (GATE1-D3, A-4).
 *
 * Production hardcoded 14 products in the JavaScript bundle while the `products`
 * table held 0 rows (X-1). This component renders ONLY what the API returns.
 *
 * Rule 3 is absolute here: when there are no products we show an explicit empty
 * state that still routes the visitor to the quote form. We do not invent
 * products, and we do not carry the old hardcoded array forward as a "fallback"
 * — a fallback would be indistinguishable from fabrication.
 *
 * Card anatomy is preserved from the baseline (P-10): category label, H3 18px,
 * unit, description, "Quote on request", "Request Quote →".
 */

/**
 * Category images. These map a category to one of the five photographs that
 * production already ships. They are DECORATION, not product data — a product
 * without its own image falls back to its category photo, exactly as production
 * behaved (C-6). We never invent a product photo.
 */
const CATEGORY_IMAGE: Record<string, string> = {
  'cleaning chemicals': '/assets/product-chemicals.jpg',
  'paper products': '/assets/product-paper.jpg',
  'janitorial supplies': '/assets/product-janitorial.jpg',
  'janitorial tools': '/assets/product-janitorial.jpg',
  'hygiene products': '/assets/product-hygiene.jpg',
  'hygiene consumables': '/assets/product-hygiene.jpg',
  'refuse bags & ppe': '/assets/product-ppe.jpg',
  'disposable ppe': '/assets/product-ppe.jpg',
  'refuse/waste supplies': '/assets/product-ppe.jpg',
};

function imageFor(p: PublicProduct): string {
  if (p.imageUrl) return p.imageUrl;
  return CATEGORY_IMAGE[(p.category ?? '').toLowerCase()] ?? '/assets/product-chemicals.jpg';
}

export function ProductCard({ product }: { product: PublicProduct }) {
  return (
    <article className="card flex flex-col">
      <img
        src={imageFor(product)}
        alt={product.name}
        width={400}
        height={260}
        loading="lazy"
        className="h-48 w-full object-cover"
      />
      <div className="flex flex-1 flex-col p-6">
        {product.category && (
          <p className="text-xs font-semibold uppercase tracking-wide text-green-accent">
            {product.category}
          </p>
        )}
        <h3 className="mt-2 font-heading text-lg font-bold text-navy">{product.name}</h3>
        {product.unit && <p className="mt-1 text-sm text-charcoal/70">{product.unit}</p>}
        {product.description && (
          <p className="mt-3 flex-1 text-sm text-charcoal/80">{product.description}</p>
        )}
        <p className="mt-4 text-sm font-semibold text-charcoal">Quote on request</p>
        <Link
          href={`/quote?product=${encodeURIComponent(product.name)}`}
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-green-accent hover:underline"
        >
          Request Quote <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}

export function ProductGrid({ limit, category }: { limit?: number; category?: string }) {
  const query = useQuery({
    queryKey: ['products', category ?? 'all'],
    queryFn: () => fetchProducts(category),
    staleTime: 5 * 60 * 1000,
  });

  if (query.isPending) {
    return (
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: limit ?? 6 }).map((_, i) => (
          <div key={i} className="card">
            <Skeleton className="h-48 w-full" />
            <div className="space-y-3 p-6">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        title="We could not load the catalogue"
        body="This is a problem on our side, not yours. Please try again, or send us your requirements directly and we will quote from your list."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const products = limit ? query.data.data.slice(0, limit) : query.data.data;

  if (products.length === 0) {
    return (
      <EmptyState
        title="Our catalogue is being updated"
        body="We supply a full range of cleaning, hygiene, paper and janitorial products. Send us your requirements and we will quote directly from your list."
        action={
          <Link href="/quote" className="btn btn-primary btn-hero">
            Request a Quote
          </Link>
        }
      />
    );
  }

  return (
    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
      {products.map((p) => (
        <ProductCard key={p.id} product={p} />
      ))}
    </div>
  );
}
